import { scoreFilterLabel } from "./score";
import { FINANCIAL_ASSET_LABELS, IndustryIdToNameMap, OccupationIdToNameMap } from "../types/Tag";
import { LENGTH_OF_SERVICE_OPTIONS, LOAN_AMOUNT_MULTIPLE_OPTIONS, SearchFormType } from "../types/UsersSearch";

const formatRange = (min: number | "undefined", max: number | "undefined", unit?: string) => {
  if (min === "undefined" && max === "undefined") return "";
  const left = min === "undefined" ? "-" : String(min);
  const right = max === "undefined" ? "-" : String(max);
  return `${left}〜${right}${unit ?? ""}`;
};

export interface UserSearchSummaryOptions {
  showUserScore: boolean;
  interestedPropertyTypeNameMap: Record<number, string>;
}

// 一覧の絞り込み表示と、条件指定の一括提案画面の両方から使う。表記がずれないよう共通化している。
export const buildUserSearchSummaryParts = (
  data: SearchFormType,
  options: UserSearchSummaryOptions
): string[] => {
  const { showUserScore, interestedPropertyTypeNameMap } = options;
  const parts: string[] = [];

  if (data.userId) parts.push(`ID:${data.userId}`);
  if (showUserScore && data.score.length > 0) {
    parts.push(`ユーザースコア:${data.score.map((value) => scoreFilterLabel(value)).join("、")}`);
  }

  const age = formatRange(data.minAge, data.maxAge, "歳");
  if (age) parts.push(`年齢:${age}`);

  const income = formatRange(data.minIncome, data.maxIncome, "万円");
  if (income) parts.push(`年収:${income}`);

  if (data.loanAmountMultiple !== "undefined") {
    const multipleLabel = LOAN_AMOUNT_MULTIPLE_OPTIONS.find(
      (option) => option.value === data.loanAmountMultiple
    )?.label;
    parts.push(`借入額:${multipleLabel ?? `～${data.loanAmountMultiple}倍`}`);
  }

  if (data.lengthOfService !== "undefined") {
    const lengthLabel = LENGTH_OF_SERVICE_OPTIONS.find((option) => option.value === data.lengthOfService)?.label;
    parts.push(`勤続:${lengthLabel ?? data.lengthOfService}`);
  }
  if (data.useEstimatedLengthOfService) {
    parts.push("推定勤続ON");
  }

  if (data.financialAsset.length > 0) {
    parts.push(`金融資産:${data.financialAsset.map((asset) => FINANCIAL_ASSET_LABELS[asset]).join("、")}`);
  }

  const holding = formatRange(data.minHoldingPropertyCount, data.maxHoldingPropertyCount, "件");
  if (holding) parts.push(`保有物件:${holding}`);

  if (data.interestList.length > 0) {
    const names = data.interestList
      .map((id) => interestedPropertyTypeNameMap[id])
      .filter(Boolean)
      .join("、");
    parts.push(`興味:${names || `${data.interestList.length}件`}`);
  }

  if (data.occupationList.length > 0) {
    const names = data.occupationList
      .map((id) => OccupationIdToNameMap[id as keyof typeof OccupationIdToNameMap])
      .filter(Boolean)
      .join("、");
    parts.push(`職業:${names || `${data.occupationList.length}件`}`);
  }

  if (data.industryList.length > 0) {
    const names = data.industryList
      .map((id) => IndustryIdToNameMap[id as keyof typeof IndustryIdToNameMap])
      .filter(Boolean)
      .join("、");
    parts.push(`業種:${names || `${data.industryList.length}件`}`);
  }

  if (data.has_other_information) {
    parts.push("コメントあり");
  }

  if (data.address_prefectures && data.address_prefectures.length > 0) {
    parts.push(`居住地:${data.address_prefectures.join("、")}`);
  }

  return parts;
};

export const buildUserSearchSummary = (data: SearchFormType, options: UserSearchSummaryOptions): string =>
  buildUserSearchSummaryParts(data, options).join(" / ");
